/** @format */

import { isBrowser } from "./isBrowser";

/**
 * SSR-safe storage interface
 * Provides localStorage-like API that works in both browser and server environments
 */
export interface StorageInterface {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  clear(): void;
}

/**
 * Memory storage implementation for SSR
 * Stores data in memory during SSR, but doesn't persist
 */
class MemoryStorage implements StorageInterface {
  private store: Map<string, string> = new Map();

  getItem(key: string): string | null {
    return this.store.get(key) ?? null;
  }

  setItem(key: string, value: string): void {
    this.store.set(key, value);
  }

  removeItem(key: string): void {
    this.store.delete(key);
  }

  clear(): void {
    this.store.clear();
  }
}

// Create memory storage instance for SSR
const memoryStorage = new MemoryStorage();

/**
 * SSR-safe storage wrapper
 * Uses localStorage in browser, memory storage in SSR
 */
export const safeStorage: StorageInterface = {
  getItem: (key: string): string | null => {
    if (isBrowser()) {
      try {
        return localStorage.getItem(key);
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn("localStorage.getItem failed:", error);
        }
        return null;
      }
    }
    return memoryStorage.getItem(key);
  },

  setItem: (key: string, value: string): void => {
    if (isBrowser()) {
      try {
        localStorage.setItem(key, value);
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn("localStorage.setItem failed:", error);
        }
      }
    } else {
      memoryStorage.setItem(key, value);
    }
  },

  removeItem: (key: string): void => {
    if (isBrowser()) {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn("localStorage.removeItem failed:", error);
        }
      }
    } else {
      memoryStorage.removeItem(key);
    }
  },

  clear: (): void => {
    if (isBrowser()) {
      try {
        localStorage.clear();
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn("localStorage.clear failed:", error);
        }
      }
    } else {
      memoryStorage.clear();
    }
  },
};
